import {
    FetchState,
    UserInvestmentFeeAcknowledgementResponse,
    UserInvestmentFeeAcknowledgementState,
    DrivewealthAccountManagementType
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import * as drivewealthService from "@/shared/api/drivewealthService";
import { useImpersonatorStore } from "@/shared/store/impersonator";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useDrivewealthStore } from "@/shared/store/drivewealth";
import { useHsabAccountsStore } from "@/employee/store/hsabAccounts";
import { useEnrollmentStore } from "@/employee/store/enrollment";

export const initialState: UserInvestmentFeeAcknowledgementState = {
    fetchState: FetchState.Uninitialized,
    postFetchState: FetchState.Uninitialized,
    signedAcknoledgements: {
        signedChoiceFee: true
    }
};

export const useUserInvestmentFeeAcknowledgementStore = defineStore("userInvestmentFeeAcknowledgementStore", {
    persist: {
        storage: localStorage,
        paths: storeHelper.getStoragePaths("userInvestmentFeeAcknowledgement"),
    },
    state: () => (_.cloneDeep(initialState)),
    getters: {
        isLoaded: () => state => state.fetchState === FetchState.Loaded,
        featureEnabledForUser () {
            const impersonationStore = useImpersonatorStore();
            const featureFlagStore = useFeatureFlagStore();
            const drivewealthStore = useDrivewealthStore();
            const enrollmentStore = useEnrollmentStore();
            const hsabAccountsStore = useHsabAccountsStore();

            let hasChoiceFee = false;
            if (enrollmentStore.feeSchedule.investmentFees.length > 0) {
                const choiceFee = _.find(enrollmentStore.feeSchedule.investmentFees, x => x.accountType === "SELF");

                hasChoiceFee = choiceFee && choiceFee.annualBasis > 0;
            }

            let hasChoiceAccount = false;
            if (drivewealthStore.accountSummariesV2Loaded) {
                hasChoiceAccount = _.find(drivewealthStore.accountSummariesV2,
                    x => x.accountManagementType === DrivewealthAccountManagementType.OpenBrokerage) !== undefined;
            }

            return featureFlagStore.flags.bend29826EnableUserInvestmentFeeAcknowledgementModal &&
                hasChoiceAccount &&
                hsabAccountsStore.userHasActiveHsaAccounts &&
                hasChoiceFee &&
                !impersonationStore.impersonationIsActive;
        },
        displayChoiceFeeModal () {
            return this.isLoaded &&
                !this.signedAcknoledgements.signedChoiceFee;
        },
    },
    actions: {
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setPostFetchState (fetchState: FetchState) {
            this.postFetchState = fetchState;
        },
        setResponseData (response: UserInvestmentFeeAcknowledgementResponse) {
            this.signedAcknoledgements = response;
        },
        async getSignedAcknowledgements (): Promise<any> {
            if (this.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setFetchState(FetchState.Loading);

            return drivewealthService.getSignedAcknowledgements()
                .then(result => {
                    this.setResponseData(result.data);
                    this.setFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        async postChoiceFeeSignedAcknowledgement (): Promise<any> {
            if (this.postFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setPostFetchState(FetchState.Loading);

            return drivewealthService.postChoiceFeeSignedAcknowledgement()
                .then(result => {
                    this.setResponseData(result.data);
                    this.setPostFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
    }
});
